/**
 * PaymentAnalyticsTab
 * Aggregate view of payment performance: collected, outstanding, deductions,
 * trend over time, breakdown by milestone type, and outstanding projects list.
 *
 * Accessible to: system_admin, admin, director, manager, operation_head
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { paymentAnalyticsAPI } from '../../../services/api';
import { formatCurrency, formatRelativeTime, formatDelta, formatDate } from '../../../utils/formatters';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import TableSkeleton from '../../common/TableSkeleton';
import SegmentedControl from '../../common/SegmentedControl';
import KpiCard, { DeltaChip } from '../../common/KpiCard';
import Sparkline from '../../common/Sparkline';
import BottomSheet from '../../common/BottomSheet';
import TabNavigation from '../../common/TabNavigation';
import ProgressBar from '../../common/ProgressBar';
import PaymentAnalyticsSkeleton from '../PaymentAnalyticsSkeleton';
import AtRiskBand from '../AtRiskBand';
import PaymentAnalyticsFilters from '../PaymentAnalyticsFilters';
import PaymentsDrilldownModal from '../PaymentsDrilldownModal';
import { exportRowsToCsv } from '../../../services/exportCsv';
import useDebounce from '../../../hooks/useDebounce';
import { toast } from 'react-toastify';

const QUICK_RANGES = [
  { value: 'last_7', label: '7D', ariaLabel: 'Last 7 days' },
  { value: 'last_30', label: '30D', ariaLabel: 'Last 30 days' },
  { value: 'this_quarter', label: 'QTD', ariaLabel: 'Quarter to date' },
  { value: 'ytd', label: 'YTD', ariaLabel: 'Year to date' },
  { value: 'all_time', label: 'All', ariaLabel: 'All time' },
  { value: 'custom', label: 'Custom', ariaLabel: 'Custom range' }
];

const GROUP_BY_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' }
];

const MOBILE_VIEWS = [
  { id: 'overview', label: 'Overview' },
  { id: 'trend', label: 'Trend' },
  { id: 'breakdown', label: 'Breakdown' },
  { id: 'projects', label: 'Projects' }
];

const VALID_RANGES = new Set(QUICK_RANGES.map(r => r.value));
const VALID_GROUPS = new Set(GROUP_BY_OPTIONS.map(g => g.value));
const VALID_FILTERS = new Set(['all', 'outstanding', 'partial', 'completed']);
const VALID_SORTS = new Set(['remaining_desc', 'remaining_asc', 'collected_desc', 'project_number']);
const VALID_VIEWS = new Set(MOBILE_VIEWS.map(v => v.id));

const DEFAULTS = {
  range: 'ytd',
  group: 'month',
  filter: 'outstanding',
  sort: 'remaining_desc',
  view: 'overview'
};

const MILESTONE_LABELS = {
  lock_price: 'Lock Price',
  site_commencement: 'Site Commencement',
  progress: 'Progress Payment',
  final_completion: 'Final Completion',
  variation_order: 'Variation Orders'
};

const MILESTONE_COLORS = {
  lock_price: '#f59e0b',
  site_commencement: '#3b82f6',
  progress: '#8b5cf6',
  final_completion: '#10b981',
  variation_order: '#14b8a6'
};

function toISODate(date) {
  return date.toISOString().slice(0, 10);
}

function formatAxisCurrency(v) {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `RM ${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `RM ${(v / 1_000).toFixed(0)}k`;
  return `RM ${v}`;
}

function computeRange(preset) {
  const today = new Date();
  const end = toISODate(today);
  let start = null;

  switch (preset) {
    case 'last_7': {
      const d = new Date(today);
      d.setDate(d.getDate() - 7);
      start = toISODate(d);
      break;
    }
    case 'this_month': {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      start = toISODate(d);
      break;
    }
    case 'last_30': {
      const d = new Date(today);
      d.setDate(d.getDate() - 30);
      start = toISODate(d);
      break;
    }
    case 'this_quarter': {
      const q = Math.floor(today.getMonth() / 3);
      const d = new Date(today.getFullYear(), q * 3, 1);
      start = toISODate(d);
      break;
    }
    case 'ytd': {
      const d = new Date(today.getFullYear(), 0, 1);
      start = toISODate(d);
      break;
    }
    case 'all_time':
      return { startDate: null, endDate: null };
    default:
      return null;
  }

  return { startDate: start, endDate: end };
}

function isRowAtRisk(row) {
  return row.hasActiveNotice || (row.totalPrice > 0 && row.remaining > row.totalPrice * 0.5);
}

// Backend sort support is intentionally narrow — see paymentAnalyticsService:
// remaining supports desc/asc, collected is desc-only, project is untoggled.
const SORT_MAP = {
  project: ['project_number'],
  collected: ['collected_desc'],
  remaining: ['remaining_desc', 'remaining_asc']
};

function SortableHeader({ label, column, align, currentSort, onSort }) {
  const keys = SORT_MAP[column] || [];
  const activeIndex = keys.indexOf(currentSort);
  const isActive = activeIndex >= 0;
  const currentKey = isActive ? keys[activeIndex] : null;
  const direction = currentKey && currentKey.endsWith('_asc') ? 'asc' : isActive ? 'desc' : 'none';

  const handleClick = () => {
    if (!isActive) {
      onSort(keys[0]);
    } else if (activeIndex < keys.length - 1) {
      onSort(keys[activeIndex + 1]);
    } else {
      onSort(DEFAULTS.sort);
    }
  };

  const ariaSort = direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none';
  const alignClass = align === 'right' ? 'text-right' : 'text-left';
  const justifyClass = align === 'right' ? 'justify-end' : 'justify-start';

  return (
    <th className={`py-2 pr-3 font-normal ${alignClass}`} aria-sort={ariaSort}>
      <button
        type="button"
        onClick={handleClick}
        className={`inline-flex items-center gap-1 w-full ${justifyClass} hover:text-gray-900 ${isActive ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
      >
        <span>{label}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isActive ? '' : 'opacity-30'}
          style={direction === 'asc' ? { transform: 'rotate(180deg)' } : undefined}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
    </th>
  );
}

function ProjectCard({ row, onViewPayments, siteLinkState }) {
  const header = row.siteDatabaseId ? (
    <Link to={`/sites/${row.siteDatabaseId}`} state={siteLinkState} className="text-blue-600 hover:underline font-medium">
      {row.projectNumber}
    </Link>
  ) : (
    <span className="text-gray-700 font-medium">{row.projectNumber}</span>
  );
  const remainingClass = isRowAtRisk(row) ? 'text-orange-600' : 'text-gray-900';
  const isCompleted = row.remaining === 0 && row.totalPrice > 0;
  const collectedPct = row.totalPrice > 0 ? (row.totalPaid / row.totalPrice) * 100 : 0;

  return (
    <div className="border border-gray-200 rounded-md p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {header}
          <div className="text-xs text-gray-500 truncate">{row.clientName || '—'}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs text-gray-400">Remaining</div>
          <div className={`text-base font-semibold tabular-nums ${remainingClass}`}>{formatCurrency(row.remaining)}</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-gray-400">Total</div>
          <div className="text-gray-700 tabular-nums">{formatCurrency(row.totalPrice)}</div>
        </div>
        <div>
          <div className="text-gray-400">Collected</div>
          <div className="text-gray-900 tabular-nums">{formatCurrency(row.totalPaid)}</div>
          <div className="mt-1">
            <ProgressBar
              value={collectedPct}
              tone={isCompleted ? 'success' : 'neutral'}
              height="xs"
              ariaLabel={`Collected ${collectedPct.toFixed(0)}% of total`}
            />
          </div>
        </div>
        {row.hasActiveNotice && (
          <div>
            <div className="text-gray-400">Notice Issued</div>
            <div className="text-purple-700 font-medium tabular-nums">{formatCurrency(row.noticeIssuedAmount)}</div>
          </div>
        )}
        {row.totalDeducted > 0 && (
          <div>
            <div className="text-gray-400">Deducted</div>
            <div className="text-red-600 tabular-nums">{formatCurrency(row.totalDeducted)}</div>
          </div>
        )}
      </div>
      {row.nextMilestone && (
        <div className="mt-2 pt-2 border-t border-gray-100 text-xs">
          <span className="text-gray-400">Next: </span>
          <span className="text-gray-600">{row.nextMilestone.name}</span>
        </div>
      )}
      {onViewPayments && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={() => onViewPayments(row)}
            className="w-full text-sm font-medium text-blue-600 hover:text-blue-700 py-1.5"
          >
            View payments
          </button>
        </div>
      )}
    </div>
  );
}

export default function PaymentAnalyticsTab() {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const initialParams = useRef({
    range: VALID_RANGES.has(searchParams.get('range')) ? searchParams.get('range') : DEFAULTS.range,
    start: searchParams.get('start') || '',
    end: searchParams.get('end') || '',
    group: VALID_GROUPS.has(searchParams.get('group')) ? searchParams.get('group') : DEFAULTS.group,
    filter: VALID_FILTERS.has(searchParams.get('filter')) ? searchParams.get('filter') : DEFAULTS.filter,
    sort: VALID_SORTS.has(searchParams.get('sort')) ? searchParams.get('sort') : DEFAULTS.sort,
    view: VALID_VIEWS.has(searchParams.get('view')) ? searchParams.get('view') : DEFAULTS.view
  }).current;

  const [rangePreset, setRangePreset] = useState(initialParams.range);
  const [customStart, setCustomStart] = useState(initialParams.start);
  const [customEnd, setCustomEnd] = useState(initialParams.end);
  const [groupBy, setGroupBy] = useState(initialParams.group);
  const [mobileView, setMobileView] = useState(initialParams.view);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const debouncedSearch = useDebounce(search, 300);
  const [drilldownRow, setDrilldownRow] = useState(null);

  const [summary, setSummary] = useState(null);
  const [prevSummary, setPrevSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [projectsData, setProjectsData] = useState(null);
  const [projectsFilter, setProjectsFilter] = useState(initialParams.filter);
  const [projectsSort, setProjectsSort] = useState(initialParams.sort);

  const [loading, setLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [, setTick] = useState(0);

  const effectiveRange = useMemo(() => {
    if (rangePreset === 'custom') {
      return {
        startDate: customStart || null,
        endDate: customEnd || null
      };
    }
    return computeRange(rangePreset) || {};
  }, [rangePreset, customStart, customEnd]);

  // Previous comparable period: same duration shifted back. Null when the
  // range is open-ended (no delta chip in "All time" or invalid custom).
  const previousRange = useMemo(() => {
    const { startDate, endDate } = effectiveRange;
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start) || isNaN(end)) return null;
    const ms = end - start;
    if (ms < 0) return null;
    const prevEnd = new Date(start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd.getTime() - ms);
    return {
      startDate: toISODate(prevStart),
      endDate: toISODate(prevEnd)
    };
  }, [effectiveRange]);

  const deltaTooltip = useMemo(() => {
    if (!previousRange) return undefined;
    return `vs ${formatDate(previousRange.startDate)} – ${formatDate(previousRange.endDate)}`;
  }, [previousRange]);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (effectiveRange.startDate) params.startDate = effectiveRange.startDate;
      if (effectiveRange.endDate) params.endDate = effectiveRange.endDate;

      const prevParams = previousRange
        ? { startDate: previousRange.startDate, endDate: previousRange.endDate }
        : null;

      const [summaryRes, trendRes, breakdownRes, prevSummaryRes] = await Promise.all([
        paymentAnalyticsAPI.getSummary(params),
        paymentAnalyticsAPI.getTrend({ ...params, groupBy }),
        paymentAnalyticsAPI.getBreakdownByMilestoneType(params),
        prevParams ? paymentAnalyticsAPI.getSummary(prevParams) : Promise.resolve(null)
      ]);

      setSummary(summaryRes.data.data);
      setTrend(trendRes.data.data);
      setBreakdown(breakdownRes.data.data);
      setPrevSummary(prevSummaryRes ? prevSummaryRes.data.data : null);
      setLastUpdatedAt(new Date());
    } catch (err) {
      console.error('Failed to load payment analytics:', err);
      toast.error(err.response?.data?.error || 'Failed to load payment analytics');
    } finally {
      setLoading(false);
    }
  }, [effectiveRange, previousRange, groupBy]);

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const res = await paymentAnalyticsAPI.getProjects({
        filter: projectsFilter,
        sort: projectsSort,
        limit: 100
      });
      setProjectsData(res.data.data);
    } catch (err) {
      console.error('Failed to load projects breakdown:', err);
      toast.error(err.response?.data?.error || 'Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  }, [projectsFilter, projectsSort]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Sync state → URL (merges into existing params, preserves ?tab= owned by Dashboard)
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const apply = (key, value, defaultValue) => {
        if (value && value !== defaultValue) next.set(key, value);
        else next.delete(key);
      };
      apply('range', rangePreset, DEFAULTS.range);
      apply('start', rangePreset === 'custom' ? customStart : '', '');
      apply('end', rangePreset === 'custom' ? customEnd : '', '');
      apply('group', groupBy, DEFAULTS.group);
      apply('filter', projectsFilter, DEFAULTS.filter);
      apply('sort', projectsSort, DEFAULTS.sort);
      apply('view', mobileView, DEFAULTS.view);
      apply('q', debouncedSearch.trim(), '');
      return next;
    }, { replace: true });
  }, [rangePreset, customStart, customEnd, groupBy, projectsFilter, projectsSort, mobileView, debouncedSearch, setSearchParams]);

  const trendChartData = useMemo(() => {
    if (!trend || !Array.isArray(trend.series)) return [];
    return trend.series.map(point => {
      const d = new Date(point.period);
      let label;
      if (groupBy === 'day') label = d.toLocaleDateString();
      else if (groupBy === 'week') label = d.toLocaleDateString();
      else label = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
      return {
        label,
        Collected: point.collected,
        Deductions: point.deductions
      };
    });
  }, [trend, groupBy]);

  const visibleRows = useMemo(() => {
    if (!projectsData?.rows) return [];
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return projectsData.rows;
    return projectsData.rows.filter(r =>
      (r.projectNumber || '').toLowerCase().includes(q) ||
      (r.clientName || '').toLowerCase().includes(q)
    );
  }, [projectsData, debouncedSearch]);

  const atRiskRows = useMemo(() => {
    // Hide when the filter explicitly excludes in-progress work
    if (projectsFilter === 'completed') return [];
    if (!projectsData?.rows) return [];
    return projectsData.rows
      .filter(r => r.hasActiveNotice && r.remaining > 0)
      .sort((a, b) => (b.noticeIssuedAmount || 0) - (a.noticeIssuedAmount || 0))
      .slice(0, 3);
  }, [projectsData, projectsFilter]);

  const collectedSparkSeries = useMemo(() => {
    if (!trend || !Array.isArray(trend.series)) return undefined;
    return trend.series.map(p => p.collected);
  }, [trend]);

  const deductionsSparkSeries = useMemo(() => {
    if (!trend || !Array.isArray(trend.series)) return undefined;
    return trend.series.map(p => p.deductions);
  }, [trend]);

  const breakdownChartData = useMemo(() => {
    if (!breakdown) return [];
    return Object.keys(breakdown).map(key => ({
      key,
      name: MILESTONE_LABELS[key] || key,
      value: breakdown[key] || 0
    }));
  }, [breakdown]);

  if (loading && !summary) {
    return <PaymentAnalyticsSkeleton />;
  }

  const activeRangeLabel = QUICK_RANGES.find(r => r.value === rangePreset)?.ariaLabel
    || QUICK_RANGES.find(r => r.value === rangePreset)?.label
    || 'Custom';

  const filtersValue = { rangePreset, customStart, customEnd };
  const handleFiltersChange = (patch) => {
    if ('rangePreset' in patch) setRangePreset(patch.rangePreset);
    if ('customStart' in patch) setCustomStart(patch.customStart);
    if ('customEnd' in patch) setCustomEnd(patch.customEnd);
  };

  // Carried on Links to /sites/:id so SiteDetailPage's back button returns
  // the user to this exact URL (filters/sort/search preserved via query).
  const siteLinkState = {
    from: `${location.pathname}${location.search}`,
    fromLabel: 'Payment Analytics'
  };

  const desktopKpiRow = (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiCard
        label="Collected (in range)"
        value={formatCurrency(summary?.totalCollected || 0)}
        delta={prevSummary ? formatDelta(summary?.totalCollected, prevSummary?.totalCollected) : null}
        deltaTooltip={deltaTooltip}
        sparklineData={collectedSparkSeries}
        sparklineColor="#10b981"
        tone="positive"
        subtext={`Base ${formatCurrency(summary?.totalMilestoneCollected || 0)} · VO ${formatCurrency(summary?.totalVOCollected || 0)}`}
      />
      <KpiCard
        label="Deductions (in range)"
        value={formatCurrency(summary?.totalDeductions || 0)}
        delta={prevSummary ? formatDelta(summary?.totalDeductions, prevSummary?.totalDeductions) : null}
        deltaTooltip={deltaTooltip}
        sparklineData={deductionsSparkSeries}
        sparklineColor="#ef4444"
        tone="negative"
        subtext={`Refund ${formatCurrency(summary?.deductionsByType?.refund || 0)} · Penalty ${formatCurrency(summary?.deductionsByType?.late_penalty_interest || 0)}`}
      />
      <KpiCard
        label="Outstanding (lifetime)"
        value={formatCurrency(summary?.totalOutstanding || 0)}
        subtext={`Across ${summary?.activeProjectsCount || 0} won projects`}
      />
      <KpiCard
        label="Collection Rate"
        value={`${(summary?.collectionRate || 0).toFixed(2)}%`}
        delta={prevSummary ? formatDelta(summary?.collectionRate, prevSummary?.collectionRate) : null}
        deltaTooltip={deltaTooltip}
        tone="positive"
        subtext={`${summary?.activeNoticesCount || 0} active notice(s)`}
      />
    </div>
  );

  const mobileHeroKpi = (
    <div className="space-y-3">
      <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-medium text-gray-500">Collected (in range)</div>
          {prevSummary && (
            <DeltaChip
              delta={formatDelta(summary?.totalCollected, prevSummary?.totalCollected)}
              tone="positive"
              title={deltaTooltip}
            />
          )}
        </div>
        <div className="mt-2 text-3xl font-bold tabular-nums text-gray-900">
          {formatCurrency(summary?.totalCollected || 0)}
        </div>
        <div className="mt-1 text-xs text-gray-400 break-words">
          Base {formatCurrency(summary?.totalMilestoneCollected || 0)} · VO {formatCurrency(summary?.totalVOCollected || 0)}
        </div>
        {collectedSparkSeries && collectedSparkSeries.length >= 2 && (
          <div className="mt-3">
            <Sparkline data={collectedSparkSeries} color="#10b981" height={56} ariaLabel="Collected trend" />
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <KpiCard
          label="Deductions"
          value={formatCurrency(summary?.totalDeductions || 0)}
          delta={prevSummary ? formatDelta(summary?.totalDeductions, prevSummary?.totalDeductions) : null}
          deltaTooltip={deltaTooltip}
          tone="negative"
          compact
        />
        <KpiCard
          label="Outstanding"
          value={formatCurrency(summary?.totalOutstanding || 0)}
          compact
        />
        <KpiCard
          label="Rate"
          value={`${(summary?.collectionRate || 0).toFixed(1)}%`}
          delta={prevSummary ? formatDelta(summary?.collectionRate, prevSummary?.collectionRate) : null}
          deltaTooltip={deltaTooltip}
          tone="positive"
          compact
        />
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payment Analytics</h2>
          <p className="text-sm text-gray-500">
            Collections, outstanding balances, and deductions across won projects.
          </p>
          {lastUpdatedAt && (
            <p className="mt-1 text-xs text-gray-400">
              Last updated · {formatRelativeTime(lastUpdatedAt)}
            </p>
          )}
        </div>
        {/* Desktop filters: inline pills */}
        <div className="hidden md:block">
          <PaymentAnalyticsFilters
            value={filtersValue}
            onChange={handleFiltersChange}
            ranges={QUICK_RANGES}
            layout="inline"
          />
        </div>
        {/* Mobile filters: icon button + active-range chip */}
        <div className="flex md:hidden items-center gap-2">
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(true)}
            className="inline-flex items-center gap-2 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-white"
            aria-label="Open filters"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="7" y1="12" x2="17" y2="12" />
              <line x1="10" y1="18" x2="14" y2="18" />
            </svg>
            Filters
          </button>
          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
            {activeRangeLabel}
          </span>
        </div>
      </div>

      {/* At-risk band — always visible across mobile views too */}
      <AtRiskBand rows={atRiskRows} />

      {/* Mobile tab nav */}
      {isMobile && (
        <TabNavigation
          tabs={MOBILE_VIEWS}
          activeTab={mobileView}
          onChange={setMobileView}
        />
      )}

      {/* KPI Row — desktop always; mobile on Overview */}
      {!isMobile && desktopKpiRow}
      {isMobile && mobileView === 'overview' && mobileHeroKpi}

      {/* Trend Chart */}
      {(!isMobile || mobileView === 'trend') && (
      <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Collections vs Deductions</h3>
          <SegmentedControl
            options={GROUP_BY_OPTIONS}
            value={groupBy}
            onChange={setGroupBy}
            size="sm"
            ariaLabel="Group trend by"
          />
        </div>
        {trendChartData.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center gap-3 text-gray-400 text-sm">
            <span>No data for the selected range</span>
            {rangePreset !== 'ytd' && (
              <button
                type="button"
                onClick={() => setRangePreset('ytd')}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
              >
                Try Year to date
              </button>
            )}
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  angle={isMobile || groupBy === 'day' ? -35 : 0}
                  textAnchor={isMobile || groupBy === 'day' ? 'end' : 'middle'}
                  height={isMobile || groupBy === 'day' ? 60 : 30}
                  interval="preserveStartEnd"
                  minTickGap={isMobile ? 20 : 10}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatAxisCurrency}
                  width={isMobile ? 52 : 70}
                />
                <Tooltip formatter={v => formatCurrency(v)} />
                <Legend />
                <Line type="monotone" dataKey="Collected" stroke="#10b981" strokeWidth={2} dot={!isMobile} />
                <Line type="monotone" dataKey="Deductions" stroke="#ef4444" strokeWidth={2} dot={!isMobile} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      )}

      {/* Breakdown by milestone type */}
      {(!isMobile || mobileView === 'breakdown') && (
      <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Collected by Milestone Type</h3>
        {breakdownChartData.every(d => d.value === 0) ? (
          <div className="h-48 flex flex-col items-center justify-center gap-3 text-gray-400 text-sm">
            <span>No collections in this range</span>
            {rangePreset !== 'ytd' && (
              <button
                type="button"
                onClick={() => setRangePreset('ytd')}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
              >
                Try Year to date
              </button>
            )}
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={breakdownChartData} layout="vertical" margin={{ left: isMobile ? 0 : 30, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={formatAxisCurrency} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 88 : 140}
                />
                <Tooltip formatter={v => formatCurrency(v)} />
                <Bar dataKey="value">
                  {breakdownChartData.map(entry => (
                    <Cell key={entry.key} fill={MILESTONE_COLORS[entry.key] || '#6b7280'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      )}

      {/* Projects breakdown */}
      {(!isMobile || mobileView === 'projects') && (
      <div className="bg-white rounded-lg shadow p-4 md:p-5 border border-gray-200">
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <h3 className="text-lg font-semibold text-gray-900">Projects Breakdown</h3>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 md:flex-none md:w-56">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search project # or client"
                  aria-label="Search projects"
                  className="w-full border border-gray-300 rounded-md pl-8 pr-3 py-2 text-sm"
                />
              </div>
              <select
                aria-label="Filter projects"
                value={projectsFilter}
                onChange={e => setProjectsFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm flex-1 md:flex-none min-w-0"
              >
                <option value="all">All</option>
                <option value="outstanding">Outstanding</option>
                <option value="partial">Partially paid</option>
                <option value="completed">Completed</option>
              </select>
              {/* Mobile-only sort select; desktop uses header-click */}
              <select
                aria-label="Sort projects"
                value={projectsSort}
                onChange={e => setProjectsSort(e.target.value)}
                className="md:hidden border border-gray-300 rounded-md px-3 py-2 text-sm flex-1 min-w-0"
              >
                <option value="remaining_desc">Remaining (high → low)</option>
                <option value="remaining_asc">Remaining (low → high)</option>
                <option value="collected_desc">Collected (high → low)</option>
                <option value="project_number">Project #</option>
              </select>
              <button
                type="button"
                onClick={() => {
                  const today = new Date().toISOString().slice(0, 10);
                  exportRowsToCsv(
                    visibleRows,
                    [
                      { header: 'Project #', accessor: r => r.projectNumber },
                      { header: 'Client', accessor: r => r.clientName || '' },
                      { header: 'Total Price', accessor: r => r.totalPrice || 0 },
                      { header: 'Collected', accessor: r => r.totalPaid || 0 },
                      { header: 'Deducted', accessor: r => r.totalDeducted || 0 },
                      { header: 'Notice Issued', accessor: r => r.noticeIssuedAmount || 0 },
                      { header: 'Remaining', accessor: r => r.remaining || 0 },
                      { header: 'Next Milestone', accessor: r => r.nextMilestone?.name || '' }
                    ],
                    `payment-analytics-projects-${today}.csv`
                  );
                }}
                className="inline-flex items-center gap-1.5 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50"
                aria-label="Export projects to CSV"
                disabled={visibleRows.length === 0}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {projectsLoading ? (
          <>
            <div className="md:hidden space-y-3">
              {Array(3).fill(0).map((_, i) => (
                <div key={i} className="border border-gray-200 rounded-md p-3 animate-pulse">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 space-y-2">
                      <div className="h-4 w-24 bg-gray-200 rounded" />
                      <div className="h-3 w-32 bg-gray-100 rounded" />
                    </div>
                    <div className="text-right space-y-2">
                      <div className="h-3 w-16 bg-gray-100 rounded ml-auto" />
                      <div className="h-4 w-20 bg-gray-200 rounded ml-auto" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block">
              <TableSkeleton rows={5} columns={8} />
            </div>
          </>
        ) : visibleRows.length > 0 ? (
          <>
            {/* Mobile: card list */}
            <div className="md:hidden space-y-3">
              {visibleRows.map(r => (
                <ProjectCard key={r.projectId} row={r} onViewPayments={setDrilldownRow} siteLinkState={siteLinkState} />
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm table-auto">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <SortableHeader
                      label="Project"
                      column="project"
                      currentSort={projectsSort}
                      onSort={setProjectsSort}
                    />
                    <th className="py-2 pr-2 font-normal">Client</th>
                    <th className="py-2 pr-2 text-right font-normal whitespace-nowrap">Total Price</th>
                    <SortableHeader
                      label="Collected"
                      column="collected"
                      align="right"
                      currentSort={projectsSort}
                      onSort={setProjectsSort}
                    />
                    <th className="py-2 pr-2 text-right font-normal whitespace-nowrap" title="Pending amount on an active payment notice">Notice</th>
                    <th className="py-2 pr-2 text-right font-normal whitespace-nowrap">Deducted</th>
                    <SortableHeader
                      label="Remaining"
                      column="remaining"
                      align="right"
                      currentSort={projectsSort}
                      onSort={setProjectsSort}
                    />
                    <th className="py-2 pr-2 font-normal">Next Milestone</th>
                    <th className="py-2 pl-2 font-normal text-right whitespace-nowrap"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(r => {
                    const atRisk = isRowAtRisk(r);
                    const deductedClass = r.totalDeducted > 0 ? 'text-red-600' : 'text-gray-400';
                    const remainingClass = atRisk ? 'text-orange-600' : 'text-gray-900';
                    const isCompleted = r.remaining === 0 && r.totalPrice > 0;
                    const collectedPct = r.totalPrice > 0 ? (r.totalPaid / r.totalPrice) * 100 : 0;
                    return (
                      <tr key={r.projectId} className="border-b border-gray-100 hover:bg-gray-50 group">
                        <td className="py-2 pr-2 font-medium text-gray-900 whitespace-nowrap">
                          {r.siteDatabaseId ? (
                            <Link to={`/sites/${r.siteDatabaseId}`} state={siteLinkState} className="text-blue-600 hover:underline">
                              {r.projectNumber}
                            </Link>
                          ) : (
                            <span className="text-gray-700">{r.projectNumber}</span>
                          )}
                        </td>
                        <td className="py-2 pr-2 text-gray-700 max-w-[220px] truncate" title={r.clientName || ''}>{r.clientName || '—'}</td>
                        <td className="py-2 pr-2 text-right tabular-nums whitespace-nowrap">{formatCurrency(r.totalPrice)}</td>
                        <td className="py-2 pr-2 text-right tabular-nums whitespace-nowrap">
                          <div>{formatCurrency(r.totalPaid)}</div>
                          <div className="mt-1">
                            <ProgressBar
                              value={collectedPct}
                              tone={isCompleted ? 'success' : 'neutral'}
                              height="xs"
                              ariaLabel={`Collected ${collectedPct.toFixed(0)}% of total`}
                            />
                          </div>
                        </td>
                        <td className="py-2 pr-2 text-right tabular-nums whitespace-nowrap">
                          {r.hasActiveNotice ? (
                            <span className="text-purple-700 font-medium">{formatCurrency(r.noticeIssuedAmount)}</span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className={`py-2 pr-2 text-right tabular-nums whitespace-nowrap ${deductedClass}`}>{formatCurrency(r.totalDeducted)}</td>
                        <td className={`py-2 pr-2 text-right font-semibold tabular-nums whitespace-nowrap ${remainingClass}`}>{formatCurrency(r.remaining)}</td>
                        <td className="py-2 pr-2 text-gray-600 max-w-[180px] truncate" title={r.nextMilestone ? r.nextMilestone.name : ''}>{r.nextMilestone ? r.nextMilestone.name : '—'}</td>
                        <td className="py-2 pl-2 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => setDrilldownRow(r)}
                            className="text-xs text-blue-600 hover:text-blue-700 hover:underline opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                            aria-label={`View payments for ${r.projectNumber}`}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="text-xs text-gray-400 mt-3">
              {debouncedSearch.trim()
                ? `Showing ${visibleRows.length} of ${projectsData.count} (filtered)`
                : `Showing ${visibleRows.length} of ${projectsData.count}`}
            </div>
          </>
        ) : (
          <div className="h-32 flex flex-col items-center justify-center gap-3 text-gray-400 text-sm">
            <span>
              {debouncedSearch.trim()
                ? 'No projects match your search'
                : 'No projects match this filter'}
            </span>
            {debouncedSearch.trim() ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
              >
                Clear search
              </button>
            ) : projectsFilter !== 'all' && (
              <button
                type="button"
                onClick={() => setProjectsFilter('all')}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
              >
                Clear filter
              </button>
            )}
          </div>
        )}
      </div>
      )}

      {/* Mobile filters sheet */}
      <BottomSheet
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        title="Filters"
        maxHeight="85vh"
      >
        <PaymentAnalyticsFilters
          value={filtersValue}
          onChange={handleFiltersChange}
          ranges={QUICK_RANGES}
          layout="sheet"
          onApply={() => setMobileFiltersOpen(false)}
        />
      </BottomSheet>

      <PaymentsDrilldownModal
        isOpen={!!drilldownRow}
        onClose={() => setDrilldownRow(null)}
        row={drilldownRow}
        siteLinkState={siteLinkState}
      />
    </div>
  );
}
